import {
  Copy,
  Crosshair,
  Maximize2,
  Palette,
  Play,
  RotateCcw,
  Settings2,
  Undo2,
  Volume2,
  VolumeX,
  X,
  createIcons,
} from "lucide";

import "./styles.css";
import { Soundscape } from "./audio/Soundscape.js";
import { chooseAiMove } from "./game/ai.js";
import { BLUE, EMPTY, HexplodeGame, RED, playerName } from "./game/engine.js";
import { HexplodeScene } from "./render/HexplodeScene.js";
import {
  DEFAULT_VISUAL_CONFIG,
  VISUAL_CONFIG_STORAGE_KEY,
  VISUAL_CONTROL_GROUPS,
  normalizeVisualConfig,
} from "./render/visualConfig.js";

const iconSet = { Copy, Crosshair, Maximize2, Palette, Play, RotateCcw, Settings2, Undo2, Volume2, VolumeX, X };
const renderIcons = () => createIcons({ icons: iconSet });

const elements = {
  app: document.querySelector("#app"),
  scene: document.querySelector("#scene"),
  redScore: document.querySelector("#red-score"),
  blueScore: document.querySelector("#blue-score"),
  redTiles: document.querySelector("#red-tiles"),
  blueTiles: document.querySelector("#blue-tiles"),
  redScorePanel: document.querySelector("#red-score-panel"),
  blueScorePanel: document.querySelector("#blue-score-panel"),
  turnNumber: document.querySelector("#turn-number"),
  turnLight: document.querySelector("#turn-light"),
  statusLight: document.querySelector("#status-light"),
  statusText: document.querySelector("#status-text"),
  matchLabel: document.querySelector("#match-label"),
  primedCount: document.querySelector("#primed-count"),
  chainCount: document.querySelector("#chain-count"),
  boardCount: document.querySelector("#board-count"),
  moveLog: document.querySelector("#move-log"),
  undoButton: document.querySelector("#undo-button"),
  restartButton: document.querySelector("#restart-button"),
  cameraButton: document.querySelector("#camera-button"),
  soundButton: document.querySelector("#sound-button"),
  appearanceButton: document.querySelector("#appearance-button"),
  fullscreenButton: document.querySelector("#fullscreen-button"),
  settingsButton: document.querySelector("#settings-button"),
  resultBanner: document.querySelector("#result-banner"),
  resultTitle: document.querySelector("#result-title"),
  resultDetail: document.querySelector("#result-detail"),
  rematchButton: document.querySelector("#rematch-button"),
  resultSettingsButton: document.querySelector("#result-settings-button"),
  setupDialog: document.querySelector("#setup-dialog"),
  setupForm: document.querySelector("#setup-form"),
  closeSetupButton: document.querySelector("#close-setup-button"),
  setupSummary: document.querySelector("#setup-summary"),
  difficultyFieldset: document.querySelector("#difficulty-fieldset"),
  appearancePanel: document.querySelector("#appearance-panel"),
  appearanceControls: document.querySelector("#appearance-controls"),
  appearanceValues: document.querySelector("#appearance-values"),
  closeAppearanceButton: document.querySelector("#close-appearance-button"),
  resetAppearanceButton: document.querySelector("#reset-appearance-button"),
  copyAppearanceButton: document.querySelector("#copy-appearance-button"),
};

function loadVisualConfig() {
  try {
    const saved = localStorage.getItem(VISUAL_CONFIG_STORAGE_KEY);
    return normalizeVisualConfig(saved ? JSON.parse(saved) : DEFAULT_VISUAL_CONFIG);
  } catch {
    return { ...DEFAULT_VISUAL_CONFIG };
  }
}

const audio = new Soundscape();
let visualConfig = loadVisualConfig();
let game = new HexplodeGame(3, RED);
let busy = false;
let aiThinking = false;
let soundEnabled = true;
let matchStarted = false;
let settings = {
  mode: "ai",
  difficulty: "tactical",
  quality: "cinematic",
  speed: "fast",
  sideLength: 3,
  first: "red",
};
let moveLog = [];

const scene = new HexplodeScene(elements.scene, {
  onCellSelect: (index) => playMove(index, "human"),
  canSelect: (index) => {
    if (busy || aiThinking || game.state.gameOver || elements.setupDialog.open) return false;
    if (settings.mode === "ai" && game.state.currentPlayer === BLUE) return false;
    return game.isLegal(index);
  },
  onExplosion: (owner, chainIndex) => audio.explosion(owner, chainIndex),
});
scene.setVisualConfig(visualConfig);

function chosen(name) {
  return elements.setupForm.elements[name].value;
}

const visualControlByKey = new Map(
  VISUAL_CONTROL_GROUPS.flatMap((group) => group.controls.map((control) => [control.key, control])),
);

function formatVisualValue(control, value) {
  if (control.type === "color") return value;
  const decimals = `${control.step}`.split(".")[1]?.length ?? 0;
  return Number(value).toFixed(decimals);
}

function renderAppearanceControls() {
  elements.appearanceControls.innerHTML = VISUAL_CONTROL_GROUPS.map((group, groupIndex) => `
    <section
      id="appearance-${group.id}"
      class="appearance-group"
      role="tabpanel"
      data-appearance-panel="${group.id}"
      ${groupIndex === 0 ? "" : "hidden"}
    >
      ${group.controls.map((control) => {
        const value = visualConfig[control.key];
        if (control.type === "color") {
          return `
            <div class="visual-control">
              <label for="${control.key}-picker">${control.label}</label>
              <div class="color-control">
                <input id="${control.key}-picker" type="color" value="${value}" data-visual-key="${control.key}" aria-label="${control.label} swatch" />
                <input type="text" value="${value}" data-visual-key="${control.key}" aria-label="${control.label} value" pattern="#[0-9a-fA-F]{6}" maxlength="7" />
              </div>
            </div>
          `;
        }
        return `
          <div class="visual-control">
            <label for="${control.key}-range">${control.label}</label>
            <output data-visual-output="${control.key}">${formatVisualValue(control, value)}</output>
            <input
              id="${control.key}-range"
              type="range"
              min="${control.min}"
              max="${control.max}"
              step="${control.step}"
              value="${value}"
              data-visual-key="${control.key}"
            />
          </div>
        `;
      }).join("")}
    </section>
  `).join("");
  syncAppearanceControls();
}

function syncAppearanceControls(keys = Object.keys(visualConfig)) {
  for (const key of keys) {
    const control = visualControlByKey.get(key);
    if (!control) continue;
    const value = visualConfig[key];
    elements.appearanceControls.querySelectorAll(`[data-visual-key="${key}"]`).forEach((input) => {
      input.value = control.type === "color" ? value : String(value);
    });
    const output = elements.appearanceControls.querySelector(`[data-visual-output="${key}"]`);
    if (output) output.textContent = formatVisualValue(control, value);
  }
  elements.appearanceValues.value = JSON.stringify(visualConfig, null, 2);
}

function applyVisualConfig({ persist = true, keys = Object.keys(visualConfig) } = {}) {
  visualConfig = normalizeVisualConfig(visualConfig);
  scene.setVisualConfig(visualConfig);
  syncAppearanceControls(keys);
  if (persist) localStorage.setItem(VISUAL_CONFIG_STORAGE_KEY, JSON.stringify(visualConfig));
}

function showAppearanceTab(tabId) {
  document.querySelectorAll("[data-appearance-tab]").forEach((button) => {
    button.setAttribute("aria-selected", String(button.dataset.appearanceTab === tabId));
  });
  elements.appearanceControls.querySelectorAll("[data-appearance-panel]").forEach((panel) => {
    panel.hidden = panel.dataset.appearancePanel !== tabId;
  });
}

function openAppearance() {
  elements.appearancePanel.hidden = false;
  elements.appearanceButton.setAttribute("aria-pressed", "true");
}

function closeAppearance() {
  elements.appearancePanel.hidden = true;
  elements.appearanceButton.setAttribute("aria-pressed", "false");
}

async function copyAppearanceValues() {
  const value = elements.appearanceValues.value;
  try {
    await navigator.clipboard.writeText(value);
  } catch {
    elements.appearanceValues.focus();
    elements.appearanceValues.select();
    document.execCommand("copy");
  }
  const label = elements.copyAppearanceButton.querySelector("span");
  label.textContent = "Copied";
  window.setTimeout(() => { label.textContent = "Copy values"; }, 1200);
}

function playerColor(player) {
  return player === RED ? "#ff304d" : "#28a6ff";
}

function cellLabel(index) {
  const cell = game.board.cells[index];
  return `${cell.q},${cell.r}`;
}

function updateSetupState() {
  const mode = chosen("mode");
  const board = Number(chosen("board"));
  const first = chosen("first");
  elements.difficultyFieldset.hidden = mode !== "ai";
  const opponentLabel = mode === "ai" ? "vs computer" : "two player";
  const firstLabel = first === "random" ? "random start" : `${first[0].toUpperCase()}${first.slice(1)} starts`;
  const boardLabel = board === 3 ? "Small · 19 cells" : "Large · 37 cells";
  elements.setupSummary.textContent = `${boardLabel} · ${opponentLabel} · ${firstLabel}`;
}

function updateHud(statusOverride = null) {
  const totals = game.totals();
  elements.redScore.textContent = totals.redScore;
  elements.blueScore.textContent = totals.blueScore;
  elements.redTiles.textContent = `${totals.redTiles} cell${totals.redTiles === 1 ? "" : "s"}`;
  elements.blueTiles.textContent = `${totals.blueTiles} cell${totals.blueTiles === 1 ? "" : "s"}`;
  elements.turnNumber.textContent = `TURN ${game.state.turnsPlayed + (game.state.gameOver ? 0 : 1)}`;
  elements.primedCount.textContent = totals.primed;
  elements.chainCount.textContent = game.state.lastChainCount;
  elements.boardCount.textContent = game.board.cells.length;
  elements.matchLabel.textContent = settings.mode === "ai" ? `VS AI · ${settings.difficulty.toUpperCase()}` : "LOCAL · 2P";

  const activePlayer = game.state.currentPlayer;
  const color = playerColor(activePlayer);
  elements.turnLight.style.background = color;
  elements.turnLight.style.color = color;
  elements.statusLight.style.background = color;
  elements.statusLight.style.color = color;
  elements.redScorePanel.classList.toggle("active", !game.state.gameOver && activePlayer === RED);
  elements.blueScorePanel.classList.toggle("active", !game.state.gameOver && activePlayer === BLUE);

  if (statusOverride) {
    elements.statusText.textContent = statusOverride;
  } else if (game.state.gameOver) {
    elements.statusText.textContent = game.state.winner === EMPTY ? "Match drawn" : `${playerName(game.state.winner)} wins`;
  } else if (aiThinking) {
    elements.statusText.textContent = "Blue is calculating";
  } else if (busy) {
    elements.statusText.textContent = "Cascade resolving";
  } else {
    elements.statusText.textContent = `${playerName(activePlayer)} to move`;
  }

  elements.undoButton.disabled = busy || aiThinking || game.history.length === 0;
  elements.restartButton.disabled = busy || aiThinking;
  elements.settingsButton.disabled = busy;
  elements.moveLog.innerHTML = moveLog
    .slice(0, 4)
    .map((entry) => `<li><b>${entry.label}</b><span>${entry.detail}</span></li>`)
    .join("");
  scene.setCurrentPlayer(activePlayer);
}

async function showResult() {
  elements.resultTitle.textContent = game.state.winner === EMPTY ? "Draw" : `${playerName(game.state.winner)} wins`;
  elements.resultTitle.style.color = game.state.winner === EMPTY ? "#f4c95d" : playerColor(game.state.winner);
  elements.resultDetail.textContent = `${game.state.turnsPlayed} turns · ${game.state.lastChainCount} in the final chain`;
  if (game.state.winner !== EMPTY) audio.win(game.state.winner);
  await scene.celebrate(game.state.winner);
  elements.resultBanner.hidden = false;
}

function hideResult() {
  elements.resultBanner.hidden = true;
  scene.stopCelebration();
}

async function playMove(index, source) {
  if (busy || aiThinking || !game.isLegal(index)) return;
  audio.unlock().catch(() => {});
  const result = game.applyMove(index);
  if (!result.legal) {
    updateHud("That cell belongs to the opponent");
    window.setTimeout(() => updateHud(), 550);
    return;
  }

  busy = true;
  audio.place(result.player);
  moveLog.unshift({
    label: `${playerName(result.player)} · ${cellLabel(index)}`,
    detail: result.events.length ? `${result.events.length} burst${result.events.length === 1 ? "" : "s"}` : `+1`,
  });
  updateHud(result.events.length ? "Cascade resolving" : `${playerName(result.player)} placed`);
  await scene.animateMove(result, settings.speed);

  if (game.state.gameOver) {
    updateHud();
    await showResult();
    busy = false;
    updateHud();
    return;
  }
  busy = false;
  updateHud();
  if (source !== "ai") await maybeAiMove();
}

async function maybeAiMove() {
  if (settings.mode !== "ai" || game.state.gameOver || game.state.currentPlayer !== BLUE) return;
  aiThinking = true;
  updateHud();
  const delay = settings.speed === "fast" ? 90 : 360;
  await new Promise((resolve) => window.setTimeout(resolve, delay));
  const move = chooseAiMove(game, settings.difficulty, BLUE);
  aiThinking = false;
  updateHud();
  if (move !== null) await playMove(move, "ai");
}

function resolveStartingPlayer(first) {
  if (first === "random") return Math.random() < 0.5 ? RED : BLUE;
  return first === "blue" ? BLUE : RED;
}

function startMatch() {
  settings = {
    mode: chosen("mode"),
    difficulty: chosen("difficulty"),
    quality: chosen("quality"),
    speed: chosen("speed"),
    sideLength: Number(chosen("board")),
    first: chosen("first"),
  };
  const startingPlayer = resolveStartingPlayer(settings.first);
  game = new HexplodeGame(settings.sideLength, startingPlayer);
  moveLog = [];
  busy = false;
  aiThinking = false;
  matchStarted = true;
  hideResult();
  scene.setQuality(settings.quality);
  scene.buildBoard(game.board);
  scene.sync(game.state.owners, game.state.levels, { force: true });
  scene.setCurrentPlayer(game.state.currentPlayer);
  scene.frameBoard(false);
  elements.setupDialog.close();
  elements.app.dataset.gameReady = "true";
  updateHud();
  maybeAiMove();
}

function restartMatch() {
  const startingPlayer = resolveStartingPlayer(settings.first);
  game.reset(settings.sideLength, startingPlayer);
  moveLog = [];
  busy = false;
  aiThinking = false;
  hideResult();
  scene.buildBoard(game.board);
  scene.sync(game.state.owners, game.state.levels, { force: true });
  scene.frameBoard(true);
  updateHud();
  maybeAiMove();
}

function openSetup() {
  if (busy) return;
  closeAppearance();
  hideResult();
  updateSetupState();
  elements.closeSetupButton.hidden = !matchStarted;
  elements.setupDialog.showModal();
}

function undoMove() {
  if (busy || aiThinking || !game.history.length) return;
  hideResult();
  const steps = settings.mode === "ai" && game.history.length >= 2 ? 2 : 1;
  const restored = game.undo(steps);
  if (!restored) return;
  moveLog.unshift({ label: "Undo", detail: `${steps} turn${steps === 1 ? "" : "s"}` });
  scene.sync(restored.owners, restored.levels, { force: true });
  updateHud();
}

function toggleSound() {
  soundEnabled = !soundEnabled;
  audio.setEnabled(soundEnabled);
  elements.soundButton.innerHTML = `<i data-lucide="${soundEnabled ? "volume-2" : "volume-x"}"></i>`;
  elements.soundButton.title = soundEnabled ? "Mute sound" : "Enable sound";
  elements.soundButton.setAttribute("aria-label", elements.soundButton.title);
  renderIcons();
}

async function toggleFullscreen() {
  if (!document.fullscreenElement) await document.documentElement.requestFullscreen?.();
  else await document.exitFullscreen?.();
}

elements.setupForm.addEventListener("change", updateSetupState);
elements.setupForm.addEventListener("submit", (event) => {
  event.preventDefault();
  startMatch();
});
elements.closeSetupButton.addEventListener("click", () => {
  if (matchStarted) elements.setupDialog.close();
});
elements.setupDialog.addEventListener("cancel", (event) => {
  if (!matchStarted) event.preventDefault();
});
elements.undoButton.addEventListener("click", undoMove);
elements.restartButton.addEventListener("click", restartMatch);
elements.cameraButton.addEventListener("click", () => scene.frameBoard(true));
elements.soundButton.addEventListener("click", toggleSound);
elements.appearanceButton.addEventListener("click", () => {
  if (elements.appearancePanel.hidden) openAppearance();
  else closeAppearance();
});
elements.closeAppearanceButton.addEventListener("click", closeAppearance);
elements.appearanceControls.addEventListener("input", (event) => {
  const input = event.target.closest("[data-visual-key]");
  if (!input) return;
  const key = input.dataset.visualKey;
  const control = visualControlByKey.get(key);
  if (!control) return;
  let value = input.value;
  if (control.type === "color") {
    value = value.trim().toLowerCase();
    if (!/^#[0-9a-f]{6}$/.test(value)) return;
  } else {
    value = Number(value);
  }
  visualConfig = { ...visualConfig, [key]: value };
  applyVisualConfig({ keys: [key] });
});
document.querySelectorAll("[data-appearance-tab]").forEach((button) => {
  button.addEventListener("click", () => showAppearanceTab(button.dataset.appearanceTab));
});
elements.resetAppearanceButton.addEventListener("click", () => {
  visualConfig = { ...DEFAULT_VISUAL_CONFIG };
  applyVisualConfig();
});
elements.copyAppearanceButton.addEventListener("click", copyAppearanceValues);
elements.fullscreenButton.addEventListener("click", toggleFullscreen);
elements.settingsButton.addEventListener("click", openSetup);
elements.rematchButton.addEventListener("click", restartMatch);
elements.resultSettingsButton.addEventListener("click", openSetup);

scene.buildBoard(game.board);
scene.sync(game.state.owners, game.state.levels, { force: true });
renderAppearanceControls();
updateSetupState();
updateHud();
renderIcons();
openSetup();

window.__hexplode = {
  get game() {
    return game;
  },
  get scene() {
    return scene;
  },
  get visualConfig() {
    return { ...visualConfig };
  },
  startMatch,
  playMove,
};
